import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Compass, PlusCircle, PawPrint } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'petius_onboarding_done_v1';

interface Step {
  icon: typeof Compass;
  title: string;
  body: string;
  tone: string;
}

export const FirstVisitOnboarding = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }
    const tm = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(tm);
  }, [user]);

  const steps: Step[] = [
    {
      icon: PawPrint,
      title: t('onboardingTour.step1Title', 'Welcome to Petius'),
      body: t(
        'onboardingTour.step1Body',
        'Share photos, track health and connect with a community of pet lovers.'
      ),
      tone: 'from-purple-500 to-blue-500',
    },
    {
      icon: PlusCircle,
      title: t('onboardingTour.step2Title', 'Tap the + button'),
      body: t(
        'onboardingTour.step2Body',
        'Use the central button on the bottom bar to create photos, log health records or post alerts.'
      ),
      tone: 'from-pink-500 to-rose-500',
    },
    {
      icon: Compass,
      title: t('onboardingTour.step3Title', 'Explore the community'),
      body: t(
        'onboardingTour.step3Body',
        'Discover services, pet-friendly places and matches in the Explore tab.'
      ),
      tone: 'from-emerald-500 to-teal-500',
    },
  ];

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* noop */
    }
    setOpen(false);
  };

  const next = () => {
    if (step >= steps.length - 1) finish();
    else setStep((s) => s + 1);
  };

  const current = steps[step];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish()}>
      <DialogContent className="max-w-sm rounded-3xl p-0 overflow-hidden">
        <div
          className={`bg-gradient-to-br ${current.tone} px-6 pt-8 pb-10 text-white flex flex-col items-center text-center`}
        >
          <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
            <Icon className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold mb-2">{current.title}</h2>
          <p className="text-sm text-white/90 leading-relaxed">{current.body}</p>
        </div>
        <div className="px-6 py-5 flex flex-col items-center gap-4">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-primary' : 'w-1.5 bg-muted'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 w-full">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={finish}
            >
              {t('onboardingTour.skip', 'Skip')}
            </Button>
            <Button size="sm" className="flex-1" onClick={next}>
              {step >= steps.length - 1
                ? t('onboardingTour.done', 'Get started')
                : t('onboardingTour.next', 'Next')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FirstVisitOnboarding;
